import React, { useEffect, useMemo, useState } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import { contasPagarApi } from '../../api/financeiro';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';

const FORMA_LABEL = { boleto: 'Boleto', pix: 'Pix', conta: 'Crédito em Conta' };
const STATUS_TOM = { pendente: 'aviso', pago: 'sucesso', cancelado: 'perigo' };
const STATUS_LABEL = { pendente: 'Pendente', pago: 'Pago', cancelado: 'Cancelado' };
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function formatarReais(v) {
  return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function hojeISO() { return new Date().toISOString().slice(0, 10); }

function montarGrade(mes, ano) {
  const primeiroDia = new Date(ano, mes - 1, 1);
  const inicioGrade = new Date(primeiroDia);
  inicioGrade.setDate(inicioGrade.getDate() - primeiroDia.getDay());
  const dias = [];
  const cursor = new Date(inicioGrade);
  for (let i = 0; i < 42; i++) {
    dias.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dias;
}

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function ContasPagarCalendario() {
  useTituloPagina('Calendário — Contas a Pagar');
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [diaSelecionado, setDiaSelecionado] = useState(hojeISO());
  const [resumoPeriodo, setResumoPeriodo] = useState([]);
  const [resumoDia, setResumoDia] = useState(null);
  const [contasDia, setContasDia] = useState([]);

  const dias = useMemo(() => montarGrade(mes, ano), [mes, ano]);
  const primeiroDiaGrade = isoLocal(dias[0]);
  const ultimoDiaGrade = isoLocal(dias[41]);

  useEffect(() => {
    contasPagarApi.resumoPeriodo(primeiroDiaGrade, ultimoDiaGrade).then(setResumoPeriodo).catch(() => {});
  }, [primeiroDiaGrade, ultimoDiaGrade]);

  const carregarDia = (data) => {
    setDiaSelecionado(data);
    contasPagarApi.resumo(data).then(setResumoDia).catch(() => {});
    contasPagarApi.listar({ data_de: data, data_ate: data }).then(setContasDia).catch(() => {});
  };

  useEffect(() => { carregarDia(diaSelecionado); }, []); // eslint-disable-line

  const totaisPorDia = useMemo(() => {
    const mapa = {};
    resumoPeriodo.forEach(r => {
      if (!mapa[r.data_vencimento]) mapa[r.data_vencimento] = { total: 0, quantidade: 0, temPendente: false, temAtrasada: false };
      mapa[r.data_vencimento].total += Number(r.total);
      mapa[r.data_vencimento].quantidade += r.quantidade;
      if (r.status === 'pendente') {
        mapa[r.data_vencimento].temPendente = true;
        if (r.data_vencimento < hojeISO()) mapa[r.data_vencimento].temAtrasada = true;
      }
    });
    return mapa;
  }, [resumoPeriodo]);

  const marcarPago = async (conta) => {
    if (!window.confirm(`Confirmar pagamento de ${formatarReais(conta.valor)} — ${conta.descricao}?`)) return;
    await contasPagarApi.pagar(conta.id);
    carregarDia(diaSelecionado);
  };

  const cancelar = async (conta) => {
    if (!window.confirm(`Cancelar o lançamento "${conta.descricao}"?`)) return;
    await contasPagarApi.cancelar(conta.id);
    carregarDia(diaSelecionado);
  };

  const mudarMes = (delta) => {
    let novoMes = mes + delta, novoAno = ano;
    if (novoMes > 12) { novoMes = 1; novoAno++; }
    if (novoMes < 1) { novoMes = 12; novoAno--; }
    setMes(novoMes); setAno(novoAno);
  };

  return (
    <div>
      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center' }}>
          <div style={{ background: COLORS.primaryBg, borderRadius: 10, padding: 14, minWidth: 150 }}>
            <div style={{ fontSize: 11, color: COLORS.primary, textTransform: 'uppercase', fontWeight: 700 }}>
              Total {diaSelecionado === hojeISO() ? 'de Hoje' : `em ${new Date(diaSelecionado + 'T00:00:00').toLocaleDateString('pt-BR')}`}
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.primary }}>{formatarReais(resumoDia?.total)}</div>
            <div style={{ fontSize: 11, color: COLORS.primary }}>{resumoDia?.quantidade || 0} lançamento(s)</div>
          </div>
          {['boleto', 'pix', 'conta'].map(forma => (
            <div key={forma} style={{ background: COLORS.bg, borderRadius: 10, padding: 14, minWidth: 130 }}>
              <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', fontWeight: 700 }}>{FORMA_LABEL[forma]}</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{formatarReais(resumoDia?.[forma]?.total)}</div>
              <div style={{ fontSize: 11, color: COLORS.textMid }}>{resumoDia?.[forma]?.quantidade || 0} lançamento(s)</div>
            </div>
          ))}
          <div style={{ background: COLORS.warningBg, borderRadius: 10, padding: 14, minWidth: 130 }}>
            <div style={{ fontSize: 11, color: COLORS.warning, textTransform: 'uppercase', fontWeight: 700 }}>Pendente</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: COLORS.warning }}>{formatarReais(resumoDia?.pendente?.total)}</div>
            <div style={{ fontSize: 11, color: COLORS.warning }}>{resumoDia?.pendente?.quantidade || 0} lançamento(s)</div>
          </div>
        </div>
      </Card>

      <Card style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Btn tamanho="sm" variante="ghost" onClick={() => mudarMes(-1)}>‹</Btn>
            <strong style={{ fontSize: 15, minWidth: 160, textAlign: 'center' }}>{MESES[mes - 1]} {ano}</strong>
            <Btn tamanho="sm" variante="ghost" onClick={() => mudarMes(1)}>›</Btn>
          </div>
          <Btn tamanho="sm" variante="ghost" onClick={() => { setMes(hoje.getMonth() + 1); setAno(hoje.getFullYear()); carregarDia(hojeISO()); }}>Hoje</Btn>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {DIAS_SEMANA.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: COLORS.textLight, padding: '4px 0' }}>{d}</div>
          ))}
          {dias.map(d => {
            const iso = isoLocal(d);
            const foraDoMes = d.getMonth() + 1 !== mes;
            const info = totaisPorDia[iso];
            const selecionado = iso === diaSelecionado;
            const ehHoje = iso === hojeISO();
            return (
              <div key={iso} onClick={() => carregarDia(iso)}
                style={{
                  minHeight: 64, borderRadius: 8, padding: 6, cursor: 'pointer',
                  border: selecionado ? `2px solid ${COLORS.primary}` : `1px solid ${COLORS.border}`,
                  background: foraDoMes ? COLORS.bg : (selecionado ? COLORS.primaryBg : '#fff'),
                  opacity: foraDoMes ? 0.5 : 1
                }}>
                <div style={{ fontSize: 12, fontWeight: ehHoje ? 800 : 600, color: ehHoje ? COLORS.primary : COLORS.text }}>{d.getDate()}</div>
                {info && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: info.temAtrasada ? COLORS.danger : (info.temPendente ? COLORS.warning : '#1a9c5c') }}>
                      {formatarReais(info.total)}
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.textLight }}>{info.quantidade} conta(s)</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: 14, borderBottom: `1px solid ${COLORS.border}`, fontWeight: 700, fontSize: 13 }}>
          Lançamentos de {new Date(diaSelecionado + 'T00:00:00').toLocaleDateString('pt-BR')}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', background: COLORS.bg }}>
              <th style={{ padding: 10 }}>Descrição</th>
              <th style={{ padding: 10 }}>Fornecedor</th>
              <th style={{ padding: 10 }}>Forma</th>
              <th style={{ padding: 10 }}>Valor</th>
              <th style={{ padding: 10 }}>Status</th>
              <th style={{ padding: 10 }} />
            </tr>
          </thead>
          <tbody>
            {contasDia.map(c => (
              <tr key={c.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: 10 }}>
                  {c.descricao}
                  {c.total_parcelas > 1 && <span style={{ color: COLORS.textMid }}> — {c.numero_parcela}/{c.total_parcelas}</span>}
                </td>
                <td style={{ padding: 10 }}>{c.fornecedor_nome || '-'}</td>
                <td style={{ padding: 10 }}>{FORMA_LABEL[c.forma_pagamento]}</td>
                <td style={{ padding: 10, fontWeight: 600 }}>{formatarReais(c.valor)}</td>
                <td style={{ padding: 10 }}><Badge tom={STATUS_TOM[c.status]}>{STATUS_LABEL[c.status]}</Badge></td>
                <td style={{ padding: 10 }}>
                  {c.status === 'pendente' && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <Btn tamanho="sm" onClick={() => marcarPago(c)}>Pagar</Btn>
                      <Btn tamanho="sm" variante="danger" onClick={() => cancelar(c)}>Cancelar</Btn>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {contasDia.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: COLORS.textMid }}>Nenhum lançamento nesse dia.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
